import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Tag, Descriptions, Divider, message, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Table } from '../../../design-system';
import clientAPI from '../../../service/ApiServiceClientPortal';
import { formatCurrencyFixed, formatCurrencyWithCode } from '../../../utils/currencyUtils';

const BRAND_BLACK = '#1B1B1B';

const styles = {
    backRow: {
        marginBottom: 16,
    },
    title: {
        fontSize: 22,
        fontWeight: 600,
        color: BRAND_BLACK,
        margin: '0 0 4px 0',
    },
    section: {
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        marginBottom: 16,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: BRAND_BLACK,
        marginBottom: 16,
    },
    feedbackLink: {
        color: BRAND_BLACK,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 500,
        background: 'none',
        border: 'none',
        padding: 0,
        fontSize: 14,
    },
};

const statusColors = {
    active: 'green',
    completed: 'blue',
    on_hold: 'orange',
    cancelled: 'red',
    draft: 'default',
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return '-';
    }
};

const formatClientPortalCurrency = (value, currencyCode) => {
    if (value === null || value === undefined) {
        return '-';
    }
    return currencyCode ? formatCurrencyWithCode(value, currencyCode) : formatCurrencyFixed(value);
};

const salesOrderColumns = [
    {
        title: 'SO Number',
        dataIndex: 'soNumber',
        key: 'soNumber',
        render: (text) => text || '-',
    },
    {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (text) => text || '-',
    },
    {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',
        render: (val, record) => formatClientPortalCurrency(val, record.currency?.code),
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => {
            if (!status) return '-';
            const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return <Tag>{label}</Tag>;
        },
    },
    {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: formatDate,
    },
];

const ClientProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProject = async () => {
            setLoading(true);
            try {
                const response = await clientAPI.get(`/client-portal/projects/${id}`);
                const data = response.data?.data || response.data;
                setProject(data.project || data);
            } catch (err) {
                message.error('Failed to load project details');
            } finally {
                setLoading(false);
            }
        };
        fetchProject();
    }, [id]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!project) {
        return <Empty description="Project not found" />;
    }

    const statusLabel = project.status
        ? project.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
        : '-';

    const salesOrders = project.salesOrders || [];
    const paymentStatus = project.paymentStatus;
    const paymentCurrencyCode = paymentStatus?.currencyCode || project.currency?.code;

    return (
        <div>
            <div style={styles.backRow}>
                <Button
                    variant="secondary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/client-section')}
                >
                    Back to Dashboard
                </Button>
            </div>

            <div style={styles.section}>
                <h1 style={styles.title}>{project.name || 'Untitled Project'}</h1>
                <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                    <Descriptions.Item label="Client">{project.clientName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={statusColors[project.status] || 'default'}>{statusLabel}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Start Date">{formatDate(project.startDate)}</Descriptions.Item>
                    <Descriptions.Item label="End Date">{formatDate(project.endDate)}</Descriptions.Item>
                    {project.description && (
                        <Descriptions.Item label="Description" span={2}>{project.description}</Descriptions.Item>
                    )}
                </Descriptions>
            </div>

            {paymentStatus && (
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Payment Status</h3>
                    {paymentStatus.hasMixedCurrencies ? (
                        <p style={{ margin: 0, color: '#666' }}>
                            This project has invoices or sales orders in multiple currencies. Please refer to the sales-order rows below for currency-specific amounts.
                        </p>
                    ) : (
                        <Descriptions column={{ xs: 1, sm: 3 }} size="small">
                            <Descriptions.Item label="Total Invoiced">
                                {formatClientPortalCurrency(paymentStatus.totalInvoiced, paymentCurrencyCode)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Paid">
                                {formatClientPortalCurrency(paymentStatus.totalPaid, paymentCurrencyCode)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Outstanding">
                                {formatClientPortalCurrency(paymentStatus.outstanding, paymentCurrencyCode)}
                            </Descriptions.Item>
                        </Descriptions>
                    )}
                </div>
            )}

            {salesOrders.length > 0 && (
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Sales Orders</h3>
                    <Table
                        columns={salesOrderColumns}
                        dataSource={salesOrders}
                        variant="bordered"
                        rowKey="id"
                        pagination={false}
                    />
                </div>
            )}

            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Feedback</h3>
                <p style={{ margin: '0 0 12px 0', color: '#666' }}>
                    Share your feedback on this project to help us improve.
                </p>
                <button
                    style={styles.feedbackLink}
                    onClick={() => navigate(`/client-section/project/${id}/feedback`)}
                >
                    Go to Feedback Form
                </button>
            </div>

            <Divider />
        </div>
    );
};

export default ClientProjectDetail;
